import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

/* ────────────────────────── Types ────────────────────────────────── */

export interface CardDetail {
	label: string
	value: string
}

interface CardListItemProps {
	/** Optional badge text (e.g., employee badge number, branch name) */
	badge?: string
	/** Main title/heading */
	title: string
	/** Optional subtitle text (e.g., notification message) */
	subtitle?: string
	/** Optional array of label-value pairs to display */
	details?: CardDetail[]
	/** Handler for edit button click */
	onEdit: () => void
	/** Optional handler for delete button click */
	onDelete?: () => void
}

/* ────────────────────────── Component ───────────────────────────── */

export function CardListItem({
	badge,
	title,
	subtitle,
	details,
	onEdit,
	onDelete,
}: CardListItemProps) {
	return (
		<div className="relative overflow-hidden rounded-lg border border-kaefer-light-silver bg-white">
			<div className="relative px-4 py-3">
				{/* Clickable area */}
				<div className="cursor-pointer pr-20" onClick={onEdit}>
					{/* Title row with optional badge */}
					<div className="mb-1 flex min-w-0 items-center gap-2">
						{badge && (
							<span className="inline-flex items-center rounded-full bg-kaefer-medium-grey/10 px-2.5 py-0.5 text-xs  text-kaefer-medium-grey">
								{badge}
							</span>
						)}
						<h3
							title={title}
							className="min-w-0 flex-1 truncate text-sm font-semibold text-kaefer-dark-grey sm:text-base"
						>
							{title}
						</h3>
					</div>

					{/* Optional subtitle */}
					{subtitle && (
						<span className="line-clamp-2 text-sm text-muted-foreground">
							{subtitle}
						</span>
					)}

					{/* Optional details */}
					{details && details.length > 0 && (
						<div className="mt-2 space-y-2">
							<div className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
								{details.map((detail, idx) => (
									<span key={idx} className="truncate">
										<span className="font-medium text-kaefer-dark-grey/90">
											{detail.label}:
										</span>{' '}
										<span className="truncate">{detail.value}</span>
									</span>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Action buttons */}
				<div className="absolute inset-y-0 right-4 flex items-center gap-1">
					<Button
						type="button"
						variant="ghost"
						size="icon-circle"
						onClick={onEdit}
						className="text-kaefer-dark-grey hover:bg-transparent hover:text-kaefer-dark-grey/70"
					>
						<Pencil />
					</Button>
					{onDelete && (
						<Button
							type="button"
							variant="ghost"
							size="icon-circle"
							onClick={onDelete}
							className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
						>
							<Trash2 />
						</Button>
					)}
				</div>
			</div>
		</div>
	)
}
